import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Backdrop,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Grid,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon
} from '@mui/icons-material';
import { getAllHotelsBankDetails } from '../api/adminPanelApi';

const HotelBankDetails = () => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filterRouteCreated, setFilterRouteCreated] = useState('all');
  const [filterDetailsChanged, setFilterDetailsChanged] = useState('all');

  // Fetch hotel bank details from API
  const fetchHotelBankDetails = async () => {
    setLoading(true);
    try {
      const result = await getAllHotelsBankDetails();
      if (result.success) {
        const hotelsData = result.data.data || [];
        setHotels(hotelsData);
        setFilteredHotels(hotelsData);
        setSnackbar({
          open: true,
          message: hotelsData.length === 0 
            ? 'No hotels assigned' 
            : `Loaded ${hotelsData.length} hotel${hotelsData.length !== 1 ? 's' : ''} bank details successfully`,
          severity: hotelsData.length === 0 ? 'info' : 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Failed to fetch hotel bank details',
          severity: 'error'
        });
        // If auth failure, the API interceptor should handle redirect
        if (result.error?.toLowerCase().includes('token') || result.error?.toLowerCase().includes('auth')) {
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch hotel bank details',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch hotel bank details on component mount
  useEffect(() => {
    fetchHotelBankDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter hotels when search query or filters change
  useEffect(() => {
    let filtered = hotels;

    // Search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(hotel => 
        hotel.hotel_id?.toString().includes(query) ||
        hotel.hotel_name?.toLowerCase().includes(query) ||
        hotel.bank_details?.account_number?.includes(query) ||
        hotel.bank_details?.ifsc_code?.toLowerCase().includes(query) ||
        hotel.route_account?.account_id?.toLowerCase().includes(query)
      );
    }

    // Route created filter
    if (filterRouteCreated !== 'all') {
      const routeCreated = filterRouteCreated === 'yes';
      filtered = filtered.filter(hotel => hotel.route_account?.route_created === routeCreated);
    }

    // Details changed filter
    if (filterDetailsChanged !== 'all') {
      const detailsChanged = filterDetailsChanged === 'yes';
      filtered = filtered.filter(hotel => hotel.account_details_changed === detailsChanged);
    }

    setFilteredHotels(filtered);
  }, [searchQuery, filterRouteCreated, filterDetailsChanged, hotels]);

  // Toggle row expansion
  const toggleRowExpansion = (hotelId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(hotelId)) {
      newExpanded.delete(hotelId);
    } else {
      newExpanded.add(hotelId);
    }
    setExpandedRows(newExpanded);
  };

  // Format value for display (handles boolean conversion)
  const formatValue = (key, value) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    
    const keyLower = key.toLowerCase();
    const isBooleanField = /active|isactive|is_active|enabled|disabled|status/i.test(keyLower);
    
    if (isBooleanField && (value === 0 || value === 1 || value === '0' || value === '1')) {
      return value === 1 || value === '1' ? 'Yes' : 'No';
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    return String(value);
  };

  // Calculate statistics
  const totalHotels = hotels.length;
  const routeCreatedCount = hotels.filter(h => h.route_account?.route_created).length;
  const detailsChangedCount = hotels.filter(h => h.account_details_changed).length;

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Loading Backdrop */}
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => navigate('/')} 
              sx={{ 
                color: '#FF7700',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 119, 0, 0.1)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>
              Hotel Bank Details
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchHotelBankDetails}
            disabled={loading}
            sx={{ 
              borderColor: '#FF7700',
              color: '#FF7700',
              '&:hover': {
                borderColor: '#e65100',
                backgroundColor: 'rgba(255, 119, 0, 0.04)'
              }
            }}
          >
            Refresh
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View and manage hotel bank details with route account status and comparison
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            border: '1px solid #90caf9',
            boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#1565c0', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {totalHotels}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1976d2' }}>Total Hotels</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
            border: '1px solid #a5d6a7',
            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 24, color: '#388e3c' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {routeCreatedCount}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#388e3c' }}>Route Created</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
            border: '1px solid #ffcc80',
            boxShadow: '0 2px 8px rgba(255, 152, 0, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <WarningIcon sx={{ fontSize: 24, color: '#f57c00' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#ef6c00', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {detailsChangedCount}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#f57c00' }}>Details Changed</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
            border: '1px solid #ce93d8',
            boxShadow: '0 2px 8px rgba(156, 39, 176, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SearchIcon sx={{ fontSize: 24, color: '#7b1fa2' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#6a1b9a', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {filteredHotels.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7b1fa2' }}>Filtered Results</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by Hotel ID, Hotel Name, Account Number, IFSC Code, or Account ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Route Created</InputLabel>
              <Select
                value={filterRouteCreated}
                label="Route Created"
                onChange={(e) => setFilterRouteCreated(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Details Changed</InputLabel>
              <Select
                value={filterDetailsChanged}
                label="Details Changed"
                onChange={(e) => setFilterDetailsChanged(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Hotels Table */}
      {filteredHotels.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {searchQuery || filterRouteCreated !== 'all' || filterDetailsChanged !== 'all'
              ? 'No hotels found matching your search criteria.' 
              : hotels.length === 0 
                ? 'No hotels assigned' 
                : 'No hotels match the search criteria.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Hotel ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Hotel Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Account Holder</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Account Number</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Bank Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Route Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHotels.map((hotel) => {
                const bankDetails = hotel.bank_details || {};
                const routeAccount = hotel.route_account || {};
                const isExpanded = expandedRows.has(hotel.hotel_id);
                const hasWarning = hotel.account_details_changed;
                
                return (
                  <React.Fragment key={hotel.hotel_id}>
                    <TableRow 
                      hover
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: hasWarning ? 'rgba(255, 152, 0, 0.24)' : 'rgba(255, 119, 0, 0.06)',
                          cursor: 'pointer'
                        },
                        backgroundColor: hasWarning ? 'rgba(255, 152, 0, 0.16)' : 'transparent'
                      }}
                      onClick={() => toggleRowExpansion(hotel.hotel_id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#2E4057' }}>
                          {hotel.hotel_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ color: '#2E4057' }}>
                            {hotel.hotel_name || 'N/A'}
                          </Typography>
                          {hasWarning && (
                            <Tooltip title={`Account details changed: ${hotel.changed_fields?.join(', ') || 'N/A'}`}>
                              <WarningIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#2E4057' }}>
                          {bankDetails.account_holder_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#2E4057' }}>
                          {bankDetails.account_number || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#2E4057' }}>
                          {bankDetails.bank_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {routeAccount.route_created ? (
                          <Chip 
                            icon={<CheckCircleIcon sx={{ fontSize: 16, color: 'white' }} />}
                            label={routeAccount.account_id || 'Created'}
                            size="small"
                            sx={{ 
                              backgroundColor: '#4caf50',
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        ) : (
                          <Chip 
                            icon={<CancelIcon sx={{ fontSize: 16, color: 'white' }} />}
                            label="Not Created"
                            size="small"
                            sx={{ 
                              backgroundColor: '#f44336',
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small"
                          sx={{ 
                            color: '#FF7700',
                            '&:hover': { 
                              backgroundColor: 'rgba(255, 119, 0, 0.1)'
                            }
                          }}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
                            <Typography variant="h6" sx={{ mb: 2, color: '#2E4057', fontWeight: 600 }}>
                              Bank Details
                            </Typography>
                            
                            <Grid container spacing={3}>
                              {/* Bank Details from hotel_new */}
                              <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 3, backgroundColor: 'white' }}>
                                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2E4057', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccountBalanceIcon sx={{ fontSize: 20 }} /> Bank Details (hotel_new)
                                  </Typography>
                                  <Divider sx={{ mb: 2 }} />
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Account Holder Name:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                        {bankDetails.account_holder_name || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Account Number:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                        {bankDetails.account_number || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Bank Name:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                        {bankDetails.bank_name || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        IFSC Code:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                        {bankDetails.ifsc_code || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        PAN Number:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                        {bankDetails.pan_number || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        GST Number:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                        {bankDetails.gst_number || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Postal Code:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                        {bankDetails.postal_code || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>

                              {/* Route Account Details */}
                              <Grid item xs={12} md={6}>
                                {routeAccount.route_created ? (
                                  <Paper sx={{ p: 3, backgroundColor: 'white' }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2E4057', display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <AccountBalanceWalletIcon sx={{ fontSize: 20 }} /> Route Account (hotel_accounts)
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                      <Box>
                                        <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                          Account ID:
                                        </Typography>{' '}
                                        <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                          {routeAccount.account_id || 'N/A'}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                          Is Active:
                                        </Typography>{' '}
                                        <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                          {formatValue('isActive', routeAccount.isActive)}
                                        </Typography>
                                      </Box>
                                      {routeAccount.account_details && (
                                        <>
                                          <Divider sx={{ my: 1 }} />
                                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2E4057', mb: 1 }}>
                                            Account Details:
                                          </Typography>
                                          <Box>
                                            <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                              Contact Name:
                                            </Typography>{' '}
                                            <Typography 
                                              variant="body2" 
                                              component="span" 
                                              sx={{ 
                                                color: hasWarning && hotel.changed_fields?.includes('account_holder_name') 
                                                  ? '#ff9800' 
                                                  : 'text.secondary',
                                                fontWeight: hasWarning && hotel.changed_fields?.includes('account_holder_name') ? 600 : 400
                                              }}
                                            >
                                              {routeAccount.account_details.contact_name || 'N/A'}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                              Bank Name:
                                            </Typography>{' '}
                                            <Typography 
                                              variant="body2" 
                                              component="span" 
                                              sx={{ 
                                                color: hasWarning && hotel.changed_fields?.includes('bank_name') 
                                                  ? '#ff9800' 
                                                  : 'text.secondary',
                                                fontWeight: hasWarning && hotel.changed_fields?.includes('bank_name') ? 600 : 400
                                              }}
                                            >
                                              {routeAccount.account_details.bank_name || 'N/A'}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                              Account Number:
                                            </Typography>{' '}
                                            <Typography 
                                              variant="body2" 
                                              component="span" 
                                              sx={{ 
                                                color: hasWarning && hotel.changed_fields?.includes('account_number') 
                                                  ? '#ff9800' 
                                                  : 'text.secondary',
                                                fontWeight: hasWarning && hotel.changed_fields?.includes('account_number') ? 600 : 400
                                              }}
                                            >
                                              {routeAccount.account_details.account_number || 'N/A'}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                              IFSC Code:
                                            </Typography>{' '}
                                            <Typography 
                                              variant="body2" 
                                              component="span" 
                                              sx={{ 
                                                color: hasWarning && hotel.changed_fields?.includes('ifsc_code') 
                                                  ? '#ff9800' 
                                                  : 'text.secondary',
                                                fontWeight: hasWarning && hotel.changed_fields?.includes('ifsc_code') ? 600 : 400
                                              }}
                                            >
                                              {routeAccount.account_details.ifsc_code || 'N/A'}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                              Contact Email:
                                            </Typography>{' '}
                                            <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                              {routeAccount.account_details.contact_email || 'N/A'}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                              Contact Phone:
                                            </Typography>{' '}
                                            <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                              {routeAccount.account_details.contact_phone || 'N/A'}
                                            </Typography>
                                          </Box>
                                        </>
                                      )}
                                    </Box>
                                  </Paper>
                                ) : (
                                  <Paper sx={{ p: 3, backgroundColor: 'white', border: '1px dashed #e0e0e0' }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2E4057', display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <AccountBalanceWalletIcon sx={{ fontSize: 20, color: '#9e9e9e' }} /> Route Account
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center', 
                                      justifyContent: 'center',
                                      py: 3,
                                      textAlign: 'center'
                                    }}>
                                      <CancelIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                                      <Typography variant="body1" sx={{ color: '#757575', fontWeight: 500, mb: 0.5 }}>
                                        Route Account Not Created
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#9e9e9e', fontStyle: 'italic' }}>
                                        No route account exists for this hotel
                                      </Typography>
                                    </Box>
                                  </Paper>
                                )}
                              </Grid>

                              {/* Warning Message for Changed Details */}
                              {hasWarning && (
                                <Grid item xs={12}>
                                  <Paper sx={{ 
                                    p: 3, 
                                    backgroundColor: '#fff3e0',
                                    border: '1px solid #ffcc80'
                                  }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <WarningIcon sx={{ color: '#ff9800', fontSize: 24 }} />
                                      <Typography variant="h6" sx={{ color: '#f57c00', fontWeight: 600 }}>
                                        Account Details Changed
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: '#2E4057', mb: 2 }}>
                                      The following fields differ between <strong>hotel_new</strong> and <strong>hotel_accounts</strong> tables:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                      {hotel.changed_fields?.map((field, index) => (
                                        <Chip 
                                          key={index}
                                          label={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                          size="small"
                                          sx={{ 
                                            backgroundColor: '#ff9800',
                                            color: 'white',
                                            fontWeight: 500
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Paper>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelBankDetails;

